import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, FileDown, Plus } from "lucide-react";
import client, { extractError } from "@/api";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function InvoiceDetail({ id, onBack }) {
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    client.get(`/counterparty-invoices/${id}`).then((r) => setInv(r.data)).catch((err) => setError(extractError(err)));
  }, [id]);
  if (error) return <div className="form-error">{error}</div>;
  if (!inv) return <div className="loading-block">Loading…</div>;
  const download = () => window.open(`${API}/api/counterparty-invoices/${id}/pdf`, "_blank");
  return (
    <div className="admin-company-detail" data-testid="cp-invoice-detail">
      <button type="button" className="link-button back" onClick={onBack}><ChevronLeft size={14} /> Back to invoices</button>
      <header className="detail-header">
        <div>
          <p className="eyebrow">SELLER → BUYER COMMERCIAL TAX INVOICE</p>
          <h2>{inv.invoice_number}</h2>
          <p className="muted">Your role: <strong>{inv.viewer_side}</strong>. Barter India is <strong>not</strong> a party to this document.</p>
        </div>
        <div className="chip-stack">
          <button type="button" className="primary-button" onClick={download} data-testid="cp-download-pdf"><FileDown size={13} /> Download PDF</button>
        </div>
      </header>
      <section className="detail-grid">
        <article className="wide"><p className="eyebrow">SELLER (SUPPLIER)</p><p><strong>{inv.seller_name}</strong></p><p className="muted small">{inv.seller_address}</p><p className="muted small">GSTIN: {inv.seller_gstin || "—"}</p></article>
        <article className="wide"><p className="eyebrow">BUYER (RECIPIENT)</p><p><strong>{inv.buyer_name}</strong></p><p className="muted small">{inv.buyer_address}</p><p className="muted small">GSTIN: {inv.buyer_gstin || "—"}</p></article>
      </section>
      <section className="section-block">
        <p className="eyebrow">SUPPLY DETAILS</p>
        <ul className="finance-list">
          <li><span>Taxable value</span><strong>₹{inv.taxable_value.toLocaleString("en-IN")}</strong></li>
          {inv.is_interstate ? (
            <li><span>IGST ({inv.gst_rate_percent}%)</span><strong>₹{inv.igst_amount.toLocaleString("en-IN")}</strong></li>
          ) : (
            <>
              <li><span>CGST ({inv.gst_rate_percent / 2}%)</span><strong>₹{inv.cgst_amount.toLocaleString("en-IN")}</strong></li>
              <li><span>SGST ({inv.gst_rate_percent / 2}%)</span><strong>₹{inv.sgst_amount.toLocaleString("en-IN")}</strong></li>
            </>
          )}
          <li className="total"><span>Total invoice value</span><strong>₹{inv.total.toLocaleString("en-IN")}</strong></li>
        </ul>
      </section>
    </div>
  );
}

export default function CounterpartyInvoicesPage() {
  const [items, setItems] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [i, t] = await Promise.all([
        client.get("/counterparty-invoices"),
        client.get("/transactions"),
      ]);
      setItems(i.data || []);
      setTxns((t.data || []).filter((x) => x.viewer_side === "SELLER" && (x.status === "COMPLETED" || x.status === "DISPUTED")));
    } catch (err) { setError(extractError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const uninvoicedTxns = useMemo(() => {
    const invoiced = new Set(items.map((i) => i.transaction_id));
    return txns.filter((t) => !invoiced.has(t.id));
  }, [items, txns]);

  const create = async (txnId) => {
    setBusy(true); setError(""); setMessage("");
    try { const r = await client.post(`/counterparty-invoices/${txnId}/create`, {}); setMessage(`Invoice ${r.data.invoice_number} created.`); load(); }
    catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  if (detail) return <InvoiceDetail id={detail} onBack={() => { setDetail(null); load(); }} />;

  return (
    <div className="my-listings-page" data-testid="cp-invoices-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">COUNTERPARTY TAX INVOICES</p>
          <h3>Seller ↔ Buyer commercial invoices</h3>
          <p className="muted small">These invoices are between your company and the counterparty. Barter India is <strong>not</strong> a party. They are separate from the facilitator fee invoice under Billing.</p>
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      {message && <div className="form-success">{message}</div>}
      {uninvoicedTxns.length > 0 && (
        <section className="section-block" data-testid="cp-uninvoiced">
          <p className="eyebrow">READY TO INVOICE (AS SELLER)</p>
          <ul className="finance-list">
            {uninvoicedTxns.map((t) => (
              <li key={t.id}>
                <span>{t.listing_title} · ₹{t.trade_value.toLocaleString("en-IN")}</span>
                <button type="button" className="primary-button" onClick={() => create(t.id)} disabled={busy} data-testid={`cp-create-${t.id}`}><Plus size={13} /> Issue invoice</button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="cp-invoices-table">
          <thead><tr><th>Invoice #</th><th>Role</th><th>Counterparty</th><th>Taxable</th><th>GST</th><th>Total</th><th>Status</th><th>Date</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="empty-row">No counterparty invoices yet.</td></tr>}
            {items.map((i) => (
              <tr key={i.id} data-testid={`cp-row-${i.id}`}>
                <td><strong>{i.invoice_number}</strong></td>
                <td><small>{i.viewer_side}</small></td>
                <td>{i.viewer_side === "SELLER" ? i.buyer_name : i.seller_name}</td>
                <td>₹{i.taxable_value.toLocaleString("en-IN")}</td>
                <td>₹{(i.cgst_amount + i.sgst_amount + i.igst_amount).toLocaleString("en-IN")}</td>
                <td><strong>₹{i.total.toLocaleString("en-IN")}</strong></td>
                <td><span className={`status-chip status-${i.status.toLowerCase()}`}>{i.status}</span></td>
                <td><small>{i.invoice_date ? new Date(i.invoice_date).toLocaleDateString() : "—"}</small></td>
                <td>
                  <button type="button" className="link-button" data-testid={`open-cp-${i.id}`} onClick={() => setDetail(i.id)}>Open →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
